import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { actualizarMiUsuario, getMiUsuario } from '../../services/apis';

const emptyForm = {
    nombre: '',
    contrasena: '',
    confirmar_contrasena: '',
};

export const MiUsuario = () => {
    const [usuario, setUsuario] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const cargarUsuario = useCallback(async () => {
        setLoaded(false);
        setError('');

        try {
            const response = await getMiUsuario();

            if (response?.success) {
                setUsuario(response.data);
                setForm({
                    ...emptyForm,
                    nombre: response.data.nombre || '',
                });
            } else {
                setError(response?.message || 'No s’ha pogut carregar el teu usuari.');
            }
        } catch (err) {
            console.error('Error en carregar el meu usuari:', err);
            setError('No s’ha pogut carregar el teu usuari.');
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        cargarUsuario();
    }, [cargarUsuario]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validar = () => {
        if (!form.nombre.trim()) {
            return 'El nom és obligatori.';
        }

        if (form.contrasena && form.contrasena.length < 4) {
            return 'La contrasenya ha de tindre almenys 4 caràcters.';
        }

        if (form.contrasena !== form.confirmar_contrasena) {
            return 'Les contrasenyes no coincideixen.';
        }

        return '';
    };

    const guardar = async () => {
        const validationError = validar();
        if (validationError) {
            setError(validationError);
            setSuccess('');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await actualizarMiUsuario({
                nombre: form.nombre.trim(),
                contrasena: form.contrasena,
                confirmar_contrasena: form.confirmar_contrasena,
            });

            if (response?.success) {
                localStorage.setItem('DesRepresentante', form.nombre.trim());
                window.dispatchEvent(new Event('penya-auth-changed'));
                setUsuario((prev) => ({ ...prev, nombre: form.nombre.trim() }));
                setForm((prev) => ({
                    ...prev,
                    contrasena: '',
                    confirmar_contrasena: '',
                }));
                setSuccess('Usuari actualitzat correctament.');
                return;
            }

            setError(response?.message || 'No s’ha pogut guardar.');
        } catch (err) {
            console.error('Error en guardar el meu usuari:', err);
            setError('No s’ha pogut guardar.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box className="my-user-shell fade-in-2">
            <Paper elevation={0} className="my-user-header">
                <Box>
                    <Typography className="my-user-eyebrow">El meu usuari</Typography>
                    <Typography variant="h4" className="my-user-title">
                        Perfil i accés
                    </Typography>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" className="my-user-alert">
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" className="my-user-alert">
                    {success}
                </Alert>
            )}

            {!loaded && (
                <Box className="my-user-loading">
                    <CircularProgress />
                </Box>
            )}

            {loaded && usuario && (
                <Grid container spacing={2} className="my-user-grid">
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Card className="my-user-info-card" elevation={0}>
                            <CardContent>
                                <Stack className="my-user-avatar-block">
                                    <Box className="my-user-avatar">
                                        <AccountCircleOutlinedIcon />
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" className="my-user-name">
                                            {usuario.nombre}
                                        </Typography>
                                        <Typography className="my-user-mail">
                                            {usuario.mail}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Stack className="my-user-data-list">
                                    <Box className="my-user-data-row">
                                        <EmailOutlinedIcon />
                                        <Box>
                                            <Typography>Correu</Typography>
                                            <span>{usuario.mail || 'Sense correu'}</span>
                                        </Box>
                                    </Box>
                                    <Box className="my-user-data-row">
                                        <BadgeOutlinedIcon />
                                        <Box>
                                            <Typography>Rol</Typography>
                                            <span>{usuario.nombre_rol || usuario.rol || 'Sense rol'}</span>
                                        </Box>
                                    </Box>
                                    <Box className="my-user-data-row">
                                        <BusinessOutlinedIcon />
                                        <Box>
                                            <Typography>Client</Typography>
                                            <span>{usuario.nombre_mostrar_cliente || usuario.nombre_cliente || 'Sense client'}</span>
                                        </Box>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, md: 7 }}>
                        <Card className="my-user-form-card" elevation={0}>
                            <CardContent>
                                <Typography variant="h6" className="my-user-form-title">
                                    Editar accés
                                </Typography>

                                <Stack className="my-user-form">
                                    <TextField
                                        label="Nom"
                                        name="nombre"
                                        value={form.nombre}
                                        onChange={handleChange}
                                        fullWidth
                                        required
                                        inputProps={{ maxLength: 80 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountCircleOutlinedIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label="Nova contrasenya"
                                        name="contrasena"
                                        type="password"
                                        value={form.contrasena}
                                        onChange={handleChange}
                                        fullWidth
                                        helperText="Deixa-la buida si no vols canviar-la."
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockOutlinedIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label="Confirmar contrasenya"
                                        name="confirmar_contrasena"
                                        type="password"
                                        value={form.confirmar_contrasena}
                                        onChange={handleChange}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockOutlinedIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Box className="my-user-actions">
                                        <Button
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            onClick={guardar}
                                            disabled={saving}
                                        >
                                            {saving ? 'Guardant...' : 'Guardar canvis'}
                                        </Button>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};
